import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Switch,
  Select,
  Button,
  Dialog,
  MenuItem,
  Checkbox,
  TextField,
  InputLabel,
  FormControl,
  DialogTitle,
  Autocomplete,
  DialogContent,
  DialogActions,
  CircularProgress,
  DialogContentText,
} from "@mui/material";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import promoCodeAxios from "../../../utils/promoCodeAxiosService";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const AddPromoCode = () => {
  const agGridRef = useRef<AgGridReact | null>(null);
  const [paymentMethodsList, setPaymentMethodslist] = useState<any[]>([]);
  const [productItemsList, setProductItemsList] = useState<any[]>([]);
  const [code, setCode] = useState("");
  const [campaign, setCampaign] = useState<any>(0);
  const [rowData, setRowData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // New Filter States
  const [paymentMethod, setPaymentMethod] = useState<any[]>([]);
  const [productItem, setProductItem] = useState<any[]>([]);
  const [isEnabled, setIsEnabled] = useState<boolean>(true);
  const [campaignData, setCampaignData] = useState<any>(null);
  const [campaignDataLoading, setCampaignDataLoading] =
    useState<boolean>(false);
  const [activedata, setActiveData] = useState<any>(null);
  const [Open, setOpen] = useState<boolean>(false);
  const [actionRequest, setActionRequest] = useState<Boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editPromoCodeId, setEditPromoCodeId] = useState<any>(null);

  // New Field States
  const [currency, setCurrency] = useState<string>("");
  const [discountType, setDiscountType] = useState<string>("1");
  const [discountValue, setDiscountValue] = useState<string>("");
  const [maxCouponQuantity, setMaxCouponQuantity] = useState<string>("");
  const [maxPromoCodeLimit, setMaxPromoCodeLimit] = useState<string>("");
  const [maxPromoCodeLimitDurationDays, setMaxPromoCodeLimitDurationDays] =
    useState<string>("");
  const [targetUserType, setTargetUserType] = useState<any[]>([]);
  const [orderPriceMin, setOrderPriceMin] = useState<string>("");
  const [orderPriceMax, setOrderPriceMax] = useState<string>("");
  const [perUserLimit, setPerUserLimit] = useState<string>("");
  const [perUserLimitDurationDays, setPerUserLimitDurationDays] =
    useState<string>("");

  const [startDate, setStartDate] = useState<any>("");
  const [endDate, setEndDate] = useState<any>("");

  useEffect(() => {
    getPaymentMethods();
    getProductItems();
    getCampaigns();
  }, []);

  const getPaymentMethods = async () => {
    toast.dismiss();
    try {
      const response = await promoCodeAxios.get(`/PaymentMethod/GetAll`);

      const responseData = response?.data.payload || [];
      setPaymentMethodslist(responseData);
    } catch (error) {
      console.error(error);
    }
  };
  const getProductItems = async () => {
    toast.dismiss();
    try {
      const response = await promoCodeAxios.get(`/ProductItem/GetAll`);

      const responseData = response?.data.payload || [];
      setProductItemsList(responseData);
    } catch (error) {
      console.error(error);
    }
  };
  const getCampaigns = async () => {
    toast.dismiss();
    try {
      const response = await promoCodeAxios.get(`/Campaign/GetAll`);

      const responseData = response?.data.payload || [];
      setRowData(responseData);
    } catch (error) {
      console.error(error);
    }
  };
  const getCampaignData = async (productCode: string) => {
    setCampaignDataLoading(true);
    try {
      const response = await promoCodeAxios.get(
        `/PromoCode/GetPromoCodes?ProductCode=${productCode}`,
      );

      const responseData = response?.data.payload || [];
      if (responseData && responseData.length === 0) {
        toast.info("No PromoCodes Data found for the selected Campaign");
      }
      setCampaignData(responseData);
    } catch (error) {
      console.error(error);
    } finally {
      setCampaignDataLoading(false);
    }
  };

  const validateFormFields = (): boolean => {
    toast.dismiss();
    if (
      !code ||
      !campaign?.campaignId ||
      paymentMethod.length === 0 ||
      productItem.length === 0 ||
      !currency ||
      !discountValue ||
      !maxCouponQuantity ||
      !maxPromoCodeLimit ||
      !maxPromoCodeLimitDurationDays ||
      targetUserType.length === 0 ||
      !orderPriceMin ||
      !orderPriceMax ||
      !perUserLimit ||
      !perUserLimitDurationDays
    ) {
      toast.error("Please fill all the fields");
      return false;
    }

    // Check if dates are required for Active or InActive user types
    const hasActiveOrInactive = targetUserType.some(
      (t) => t.value === "Active" || t.value === "InActive",
    );

    if (hasActiveOrInactive) {
      if (!startDate || !endDate) {
        toast.error(
          "Start Date and End Date are required for Active/InActive users",
        );
        return false;
      }
    }

    return true;
  };

  const AddCampaignData = async () => {
    if (!validateFormFields()) {
      return;
    }

    setIsLoading(true);

    // Check if dates should be included (only for Active or InActive user types)
    const hasActiveOrInactiveUserType = targetUserType.some(
      (t) => t.value === "Active" || t.value === "InActive",
    );

    const payload: any = {
      code: code,
      currency: currency,
      discountType: parseInt(discountType),
      discountValue: parseFloat(discountValue),
      maxCouponQuantity: parseInt(maxCouponQuantity),
      maxPromoCodeLimit: parseInt(maxPromoCodeLimit),
      maxPromoCodeLimitDurationDays: parseInt(maxPromoCodeLimitDurationDays),
      targetUserType: targetUserType?.map((t) => t.value)?.join(","),
      orderPriceMin: parseFloat(orderPriceMin),
      orderPriceMax: parseFloat(orderPriceMax),
      perUserLimit: parseInt(perUserLimit),
      perUserLimitDurationDays: parseInt(perUserLimitDurationDays),
      paymentMethodIds: paymentMethod?.map((c) => c.paymentMethodId),
      productItemCodeIds: productItem?.map((c) => c.productItemCodeId),
      campaignId: campaign?.campaignId,
      isActive: isEnabled,
      ...(hasActiveOrInactiveUserType && {
        startDate: startDate || null,
        endDate: endDate || null,
      }),
    };

    if (isEditMode) {
      payload.promoCodeId = editPromoCodeId;
    }

    try {
      const response = await promoCodeAxios.post(`/PromoCode/Upsert`, payload);

      toast.success(response.data?.payload);
      setIsLoading(false);

      // Refresh campaign data with the selected product code
      if (campaign?.productCode) {
        await getCampaignData(campaign.productCode);
      }

      clearForm();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.errors || "Something went wrong";
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  const userTypeOptions = [
    { label: "New", value: "New" },
    { label: "Active", value: "Active" },
    { label: "InActive", value: "InActive" },
  ];

  const discountTypeOptions = [
    { label: "Flat", value: "1" },
    { label: "Percentage", value: "2" },
  ];

  const handleEditCampaign = (campaign: any) => {
    setIsEditMode(true);
    setEditPromoCodeId(campaign.promoCodeId);
    setCode(campaign.code);
    setCampaign(
      rowData?.find((item: any) => item.name === campaign.campaignName),
    );
    setIsEnabled(campaign.isActive);

    // Populate new fields
    setCurrency(campaign.currency || "");

    // Map discount type from label to value (Flat -> 1, Percentage -> 2)
    const discountTypeValue =
      discountTypeOptions?.find(
        (option) => option.label === campaign.discountType,
      )?.value ||
      campaign.discountType?.toString() ||
      "1";
    setDiscountType(discountTypeValue);

    setDiscountValue(campaign.discountValue?.toString() || "");
    setMaxCouponQuantity(campaign.maxCouponQuantity?.toString() || "");
    setMaxPromoCodeLimit(campaign.maxPromoCodeLimit?.toString() || "");
    setMaxPromoCodeLimitDurationDays(
      campaign.maxPromoCodeLimitDurationDays?.toString() || "",
    );

    // Parse targetUserType from comma separated string
    if (campaign.targetUserType) {
      const userTypes = campaign.targetUserType
        .split(",")
        ?.map((type: string) => {
          const userTypeObj = userTypeOptions?.find(
            (u) => u.value === type.trim(),
          );
          return userTypeObj || { label: type.trim(), value: type.trim() };
        });
      setTargetUserType(userTypes);
    } else {
      setTargetUserType([]);
    }

    setOrderPriceMin(campaign.orderPriceMin?.toString() || "");
    setOrderPriceMax(campaign.orderPriceMax?.toString() || "");
    setPerUserLimit(campaign.perUserLimit?.toString() || "");
    setPerUserLimitDurationDays(
      campaign.perUserLimitDurationDays?.toString() || "",
    );

    // Populate origination countries
    if (campaign.paymentMethodCodes && campaign.paymentMethodCodes.length > 0) {
      const paymentmethod = paymentMethodsList.filter((item) =>
        campaign.paymentMethodCodes.includes(item.paymentMethodCode),
      );
      setPaymentMethod(paymentmethod);
    }

    // Populate destination countries
    if (campaign.productItemCodes && campaign.productItemCodes.length > 0) {
      const productitem = productItemsList.filter((item) =>
        campaign.productItemCodes.includes(item.code),
      );
      setProductItem(productitem);
    }

    // Populate start and end dates
    setStartDate(campaign.startDate || "");
    setEndDate(campaign.endDate || "");

    // Scroll to top to show form
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const clearForm = () => {
    setIsEditMode(false);
    setEditPromoCodeId(null);
    setCode("");
    setPaymentMethod([]);
    setProductItem([]);
    setIsEnabled(true);
    setCurrency("");
    setDiscountType("1");
    setDiscountValue("");
    setMaxCouponQuantity("");
    setMaxPromoCodeLimit("");
    setMaxPromoCodeLimitDurationDays("");
    setTargetUserType([]);
    setOrderPriceMin("");
    setOrderPriceMax("");
    setPerUserLimit("");
    setPerUserLimitDurationDays("");
    setStartDate("");
    setEndDate("");
  };

  const ActionsCellRenderer: React.FC<{ data: any }> = ({ data }) => {
    return (
      <DriveFileRenameOutlineOutlinedIcon
        onClick={() => handleEditCampaign(data)}
        style={{ width: "90px", height: "32px", cursor: "pointer" }}
      ></DriveFileRenameOutlineOutlinedIcon>
    );
  };
  const CellRenderer: React.FC<{ data: any }> = ({ data }) => {
    return (
      <DeleteIcon
        onClick={() => {
          setOpen(true);
          setActiveData(data);
        }}
        style={{ cursor: "pointer" }}
      ></DeleteIcon>
    );
  };

  const DateCellRenderer: React.FC<{ data: any; field: string }> = ({
    data,
    field,
  }) => {
    // Check if targetUserType contains Active or InActive
    const targetUserTypeStr = data.targetUserType || "";
    const hasActiveOrInactive =
      targetUserTypeStr.includes("Active") ||
      targetUserTypeStr.includes("InActive");

    // Show actual value if Active or InActive is present, otherwise show "--"
    if (hasActiveOrInactive) {
      return <span>{data[field] || "--"}</span>;
    } else {
      return <span>--</span>;
    }
  };

  const columnDefs: ColDef[] = [
    {
      field: "campaignName",
      headerName: "CampaignName",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "code",
      headerName: "PromoCode",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "currency",
      headerName: "Currency",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "discountType",
      headerName: "DiscountType",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "discountValue",
      headerName: "DiscountValue",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "maxCouponQuantity",
      headerName: "CodeQuantity",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "maxPromoCodeLimit",
      headerName: "PromoCodeUsage",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "maxPromoCodeLimitDurationDays",
      headerName: "UsagePerDurationDays",
      filter: true,
      sortable: true,
      resizable: true,
    },

    {
      field: "orderPriceMin",
      headerName: "OrderPriceMin",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "orderPriceMax",
      headerName: "OrderPriceMax",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "perUserLimit",
      headerName: "UsageLimitPerUser",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "perUserLimitDurationDays",
      headerName: "UsagePerDurationDays",
      filter: true,
      sortable: true,
      resizable: true,
    },

    {
      field: "paymentMethodCodes",
      headerName: "PaymentMethods",
      valueFormatter: (p) => p.value?.join(", "),
      resizable: true,
    },
    {
      field: "productItemCodes",
      headerName: "ProductItems",
      valueFormatter: (p) => p.value?.join(", "),
      resizable: true,
    },
    {
      field: "isActive",
      headerName: "IsActive",
      filter: true,
      sortable: true,
      resizable: true,
      cellDataType: "string",
    },
    {
      field: "targetUserType",
      headerName: "TargetUserType",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "startDate",
      headerName: "StartDate",
      filter: true,
      sortable: true,
      resizable: true,
      cellRenderer: (props: any) => (
        <DateCellRenderer data={props.data} field="startDate" />
      ),
    },
    {
      field: "endDate",
      headerName: "EndDate",
      filter: true,
      sortable: true,
      resizable: true,
      cellRenderer: (props: any) => (
        <DateCellRenderer data={props.data} field="endDate" />
      ),
    },

    { cellRenderer: ActionsCellRenderer },
    { cellRenderer: CellRenderer },
  ];

  const deleteCampaign = async (Id: any) => {
    toast.dismiss();
    setActionRequest(true);
    try {
      const res = await promoCodeAxios.delete(`/PromoCode/Remove`, {
        data: { promoCodeId: Id },
      });

      if (res.data?.payload) {
        toast.success(res.data.payload);
        // Refresh campaign data with the selected product code
        if (campaign?.productCode) {
          await getCampaignData(campaign.productCode);
        }
        // Clear form only if in edit mode
        if (isEditMode) {
          clearForm();
        }
      } else {
        toast.error("Failed to delete campaign");
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors;
      toast.error(errorMessage);
      setOpen(false);
      setActionRequest(false);
    } finally {
      setOpen(false);
      setActionRequest(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ ease: "linear", duration: 0.5 }}
    >
      <div className="boxShadow">
        <h2 className="sectionTitle">
          {isEditMode ? "Edit PromoCode" : "Add PromoCode"}
        </h2>
        <div className="boxInner">
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel style={{ background: "#fff", padding: "0 5px" }}>
                  Campaigns
                </InputLabel>
                <Select
                  className="formControl"
                  autoWidth
                  value={campaign?.campaignId || "0"}
                  onChange={(e: any) => {
                    const campaign = rowData?.find(
                      (item: any) => item.campaignId === e.target.value,
                    );
                    setCampaign(campaign);
                    if (campaign?.productCode) {
                      getCampaignData(campaign.productCode);
                    }
                  }}
                  disabled={isEditMode}
                >
                  <MenuItem value="0">Choose Campaign</MenuItem>
                  {rowData.length > 0 ? (
                    rowData?.map((item: any, index: any) => (
                      <MenuItem key={index} value={item.campaignId}>
                        {item.name}--{item.productCode}
                      </MenuItem>
                    ))
                  ) : (
                    <div
                      className="circular-loader"
                      style={{ marginLeft: "0" }}
                    >
                      <CircularProgress size={40} />
                    </div>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              item
              xs={8}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
              }}
            >
              <span style={{ fontSize: "20px" }}>PromoCode:</span>
              <span> Disable</span>
              <Switch
                checked={isEnabled}
                onChange={(e) => setIsEnabled(e.target.checked)}
              />
              <span>Enable</span>
            </Grid>
            <Grid item xs={12}>
              <fieldset>
                <legend>Data:</legend>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Promo Code"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      inputProps={{ maxLength: 40 }}
                      disabled={isEditMode}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl style={{ width: "100%" }}>
                      <InputLabel
                        style={{ background: "#fff", padding: "0 5px" }}
                      >
                        Currency
                      </InputLabel>
                      <Select
                        className="formControl"
                        value={currency}
                        onChange={(e: any) => {
                          setCurrency(e.target.value);
                        }}
                      >
                        <MenuItem value="">Select Currency</MenuItem>
                        <MenuItem value="USD">USD</MenuItem>
                        <MenuItem value="GBP">GBP</MenuItem>
                        <MenuItem value="EUR">EUR</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl style={{ width: "100%" }}>
                      <InputLabel
                        style={{ background: "#fff", padding: "0 5px" }}
                      >
                        Discount Type
                      </InputLabel>
                      <Select
                        className="formControl"
                        value={discountType}
                        onChange={(e: any) => {
                          setDiscountType(e.target.value);
                        }}
                      >
                        {discountTypeOptions?.map((option, index) => (
                          <MenuItem key={index} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label={
                        discountType === "2"
                          ? "Discount Value %"
                          : "Discount Value"
                      }
                      type="number"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Code Quantity"
                      type="number"
                      value={maxCouponQuantity}
                      onChange={(e) => setMaxCouponQuantity(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Promocode usage (number)"
                      type="number"
                      value={maxPromoCodeLimit}
                      onChange={(e) => setMaxPromoCodeLimit(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Usage per duration (Days)"
                      type="number"
                      value={maxPromoCodeLimitDurationDays}
                      onChange={(e) =>
                        setMaxPromoCodeLimitDurationDays(e.target.value)
                      }
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Order Price Min"
                      type="number"
                      value={orderPriceMin}
                      onChange={(e) => setOrderPriceMin(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Order Price Max"
                      type="number"
                      value={orderPriceMax}
                      onChange={(e) => setOrderPriceMax(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Usage limit per user (number)"
                      type="number"
                      value={perUserLimit}
                      onChange={(e) => setPerUserLimit(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Usage per duration (Days)"
                      type="number"
                      value={perUserLimitDurationDays}
                      onChange={(e) =>
                        setPerUserLimitDurationDays(e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Autocomplete
                      multiple
                      id="payment-methods"
                      options={paymentMethodsList || []}
                      disableCloseOnSelect
                      value={paymentMethod || []}
                      onChange={(event, newValue) => {
                        setPaymentMethod(newValue);
                      }}
                      getOptionLabel={(option) => option.paymentMethodCode}
                      isOptionEqualToValue={(option, value) =>
                        option.paymentMethodId === value.paymentMethodId
                      }
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.paymentMethodCode}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="PaymentMethod(s)" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Autocomplete
                      multiple
                      id="product-items"
                      options={productItemsList || []}
                      disableCloseOnSelect
                      value={productItem || []}
                      onChange={(event, newValue) => {
                        setProductItem(newValue);
                      }}
                      getOptionLabel={(option) => option.code}
                      isOptionEqualToValue={(option, value) =>
                        option.productItemCodeId === value.productItemCodeId
                      }
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.code}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="Product Items" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Autocomplete
                      multiple
                      id="target-user-type"
                      options={userTypeOptions || []}
                      disableCloseOnSelect
                      value={targetUserType || []}
                      onChange={(event, newValue) => {
                        setTargetUserType(newValue);
                      }}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                      }
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.label}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="Target User Type" />
                      )}
                    />
                  </Grid>
                  {targetUserType?.some(
                    (t) => t.value === "Active" || t.value === "InActive",
                  ) && (
                    <>
                      <Grid item xs={4}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Start Date"
                            value={startDate ? dayjs(startDate) : null}
                            onChange={(newValue) => {
                              if (newValue) {
                                const formatted = newValue.format("YYYY-MM-DD");
                                setStartDate(formatted);
                                if (endDate && formatted > endDate) {
                                  setEndDate(formatted);
                                }
                              } else {
                                setStartDate("");
                              }
                            }}
                            maxDate={endDate ? dayjs(endDate) : dayjs()}
                            disableFuture
                            slotProps={{
                              actionBar: {
                                actions: ["clear"],
                              },
                              textField: {
                                fullWidth: true,
                              },
                              field: {
                                readOnly: true,
                                onKeyDown: (e: React.KeyboardEvent) =>
                                  e.preventDefault(),
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={4}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="End Date"
                            value={endDate ? dayjs(endDate) : null}
                            onChange={(newValue) => {
                              if (newValue) {
                                const formatted = newValue.format("YYYY-MM-DD");
                                setEndDate(formatted);
                              } else {
                                setEndDate("");
                              }
                            }}
                            minDate={startDate ? dayjs(startDate) : undefined}
                            maxDate={dayjs()}
                            disableFuture
                            slotProps={{
                              actionBar: {
                                actions: ["clear"],
                              },
                              textField: {
                                fullWidth: true,
                              },
                              field: {
                                readOnly: true,
                                onKeyDown: (e: React.KeyboardEvent) =>
                                  e.preventDefault(),
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </>
                  )}
                </Grid>
                <Grid
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "15px",
                  }}
                >
                  <Button
                    variant="contained"
                    className="dButton dButton2"
                    onClick={() => {
                      AddCampaignData();
                    }}
                    disabled={isLoading}
                    sx={{
                      marginTop: "15px !important",
                      width: "280px !important",
                    }}
                  >
                    {isLoading ? <CircularProgress size={30} /> : "Submit"}
                  </Button>
                  {isEditMode && (
                    <Button
                      variant="contained"
                      className="dButton dButton2 dbuttonSec"
                      onClick={() => {
                        clearForm();
                      }}
                      disabled={isLoading}
                      sx={{
                        marginTop: "15px !important",
                        width: "280px !important",
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </Grid>
              </fieldset>
            </Grid>
          </Grid>
        </div>
      </div>
      {campaignDataLoading && (
        <div className="dummy-conatiner">
          <CircularProgress size={40} />
        </div>
      )}
      {!campaignDataLoading && campaignData && campaignData.length > 0 && (
        <div className="ag-theme-alpine">
          <AgGridReact
            ref={agGridRef}
            paginationPageSize={8}
            pagination={true}
            domLayout="autoHeight"
            rowData={campaignData}
            columnDefs={columnDefs}
            enableCellTextSelection={true}
          ></AgGridReact>
        </div>
      )}

      {activedata && (
        <Dialog
          open={Open}
          onClose={() => setOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"Delete Confirmation"}
          </DialogTitle>

          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Would you really like to Delete this Campaign?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              className="dButton dButton2 dbuttonSec"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <div style={{ width: "30px" }}></div>
            {!actionRequest && (
              <Button
                variant="contained"
                className="dButton dButton2"
                onClick={() => {
                  deleteCampaign(activedata.promoCodeId);
                }}
                autoFocus
              >
                Delete
              </Button>
            )}
            {actionRequest && (
              <Button className="dButton dButton2" variant="contained">
                <CircularProgress color="inherit" size={30} />
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}
    </motion.div>
  );
};

export default AddPromoCode;
